import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPayouts } from "../slices/payOut";
import Swal from "sweetalert2"; // Import SweetAlert
import "./VendorsPayout.css";
import { updatePayout } from "../services/api";
import noDataImage from "./../assets/icons/empty_order.svg";
import TopBar from "./../components/TopBar";
import { ShimmerDiv, ShimmerTable, ShimmerTitle } from "shimmer-effects-react";

const VendorsPayout = () => {
  const dispatch = useDispatch();
  const {
    data: vendorData,
    status,
    error,
  } = useSelector((state) => state.payouts);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [activeTab, setActiveTab] = useState("processing"); // For tab management
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  }); // Sorting state
  const rowsPerPage = 10; // Number of rows per page

  useEffect(() => {
    dispatch(fetchPayouts());
  }, [dispatch]);

  const toggleApproval = (id) => {
    console.log("Toggle approval for ID:", id);
  };

  const convertToCSV = (data) => {
    const headers = [
      "Serial No.",
      "Vendor's Name",
      "Request Date",
      "Status",
      "Requested Amount",
      "Remaining Wallet Balance",
    ];

    const csvRows = [];
    csvRows.push(headers.join(","));

    data.forEach((vendor, index) => {
      const row = [
        index + 1, // Serial No.
        vendor.vendor?.name || "Name_Not_Found", // Vendor's Name
        formatDate(vendor?.date), // Request Date
        vendor?.status, // Status
        vendor?.amount ? `€ ${vendor.amount.toFixed(2)}` : "N/A", // Requested Amount
        vendor.vendor?.walletBalance
          ? `€ ${vendor.vendor.walletBalance.toFixed(2)}`
          : "0", // Remaining Wallet Balance
      ];
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  };

  const handleDownloadCSV = () => {
    const csvData = convertToCSV(filteredData);
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `payout_report_${activeTab}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApprovalChange = (vendor) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you want to ${vendor.status === "processing" ? "disapprove" : "approve"
        } this payout?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, proceed!",
      cancelButtonText: "No, cancel!",
    }).then((result) => {
      if (result.isConfirmed) {
        updatePayout(vendor._id)
          .then(() => {
            toggleApproval(vendor._id);
            Swal.fire(
              "Updated!",
              "The payout status has been changed.",
              "success"
            );
            dispatch(fetchPayouts());
          })
          .catch((error) => {
            Swal.fire(
              "Error",
              "There was an issue updating the payout status.",
              "error"
            );
          });
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        timeZone: "UTC",
      })
      .replace(",", "");
  };

  const handleMoreClick = (vendor) => {
    setSelectedVendor(vendor);
  };

  const closeModal = () => {
    setSelectedVendor(null);
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedData = [...vendorData].sort((a, b) => {
    if (sortConfig.key === "date") {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
    const valueA = a[sortConfig.key] || "";
    const valueB = b[sortConfig.key] || "";
    return sortConfig.direction === "asc"
      ? valueA.toString().localeCompare(valueB.toString())
      : valueB.toString().localeCompare(valueA.toString());
  });

  const filteredData =
    activeTab === "processing"
      ? sortedData.filter((vendor) => vendor.status === "processing")
      : sortedData.filter((vendor) => vendor.status === "completed");

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (status === "loading") {
    return (
      <>
        <div className="flex items-center justify-between gap-8">
          <ShimmerDiv mode="dark" height={100} width={700} rounded={1} border={0} />
          <ShimmerDiv mode="dark" height={100} width={300} rounded={1} border={0} />
          {/* <ShimmerTitle mode="dark" line={3} gap={8} width={500} /> */}
        </div>
        <ShimmerTable mode="dark" row={7} col={7} border={0} borderColor={"#cbd5e1"} rounded={0.25} rowGap={16} colPadding={[10, 5, 10, 5]} />
      </>
    );
  }

  if (status === "failed") {
    return <p>Error: {error}</p>;
  }

  const renderVendorPayout = () => {
    return (
      <table className="vendors-payout-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("id")}>Serial No.</th>
            <th
              className="header-cell clickable"
              onClick={() => handleSort("vendor.name")}
            >
              Vendor's Name{" "}
              {sortConfig.key === "vendor.name" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th
              className="header-cell clickable"
              onClick={() => handleSort("date")}
            >
              Request Date{" "}
              {sortConfig.key === "date" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>Status</th>
            <th
              className="header-cell clickable"
              onClick={() => handleSort("amount")}
            >
              Requested Amount{" "}
              {sortConfig.key === "amount" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th
              className="header-cell clickable"
              onClick={() => handleSort("vendor.walletBalance")}
            >
              Remaining Wallet Balance{" "}
              {sortConfig.key === "vendor.walletBalance" &&
                (sortConfig.direction === "asc" ? "↑" : "↓")}
            </th>
            <th>Approve/Disapprove</th>
            <th>More</th>
          </tr>
        </thead>
        <tbody>
          {currentRows.map((vendor, index) => (
            <tr key={vendor?.id}>
              <td>{indexOfFirstRow + index + 1}</td>
              <td>{vendor?.vendor?.name || "Name_Not_Found"}</td>
              <td>{formatDate(vendor?.date)}</td>
              <td>{vendor?.status}</td>
              <td>{"€ " + vendor?.amount?.toFixed(2) || "N/A"}</td>
              <td>{"€ " + vendor?.vendor?.walletBalance?.toFixed(2) || "0"}</td>
              <td>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={vendor?.status !== "processing"}
                    onChange={(e) => {
                      if (activeTab === "processing") {
                        handleApprovalChange(vendor);
                      } else {
                        e.preventDefault(); // Prevents changing the state in the completed section
                      }
                    }}
                  />
                  <span className="slider round"></span>
                </label>
              </td>
              <td>
                <button
                  className="more-button"
                  onClick={() => handleMoreClick(vendor)}
                >
                  More
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <TopBar searchPlaceholder="Search Vendors Payout Here" />
      <div className="p-6">
        <div className="vendors-payout-container">
          <div className="buttons-container">
            <button
              className={`btn1 payout-btn ${activeTab === "processing" ? "active-btn" : "de-btn"
                }`}
              onClick={() => {
                setActiveTab("processing");
                setCurrentPage(1);
              }}
            >
              Processing Payouts
            </button>
            <button
              className={`btn1 payout-btn ${activeTab === "completed" ? "active-btn" : "de-btn"
                } ml-4`}
              onClick={() => {
                setActiveTab("completed");
                setCurrentPage(1);
              }}
            >
              Completed Payouts
            </button>
          </div>

          <div className="orders-content">
            {currentRows.length > 0 ? (
              renderVendorPayout()
            ) : (
              <div className="empty-orders">
                <img
                  src={noDataImage}
                  className="empty-orders-icon"
                  alt="No Data"
                />
                <h3 className="no-orders-title">
                  No vendor's payouts to show right now!
                </h3>
                <p className="no-orders-text">
                  Vendor's payout section is empty! When someone sends requests, the
                  details will be shown here.
                </p>
              </div>
            )}
          </div>

          <div className="pagination-container mt-4 mx-auto justify-between px-1">
            <button onClick={handlePreviousPage} disabled={currentPage === 1}>
              &lt; Previous
            </button>
            <span>
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={handleNextPage} disabled={currentPage === totalPages}>
              Next &gt;
            </button>
          </div>

          <div className="download-report">
            <button className="download-link" onClick={handleDownloadCSV}>
              Download Report
            </button>
          </div>

          {selectedVendor && (
            <div className="modal">
              <div className="modal-content">
                <span className="close-button" onClick={closeModal}>
                  &times;
                </span>
                <h2>Vendor Account Details</h2>
                <p>
                  <strong>Vendor Name:</strong> {selectedVendor?.vendor?.name}
                </p>
                <p>
                  <strong>Holder Name:</strong>{" "}
                  {selectedVendor?.account?.holdername}
                </p>
                <p>
                  <strong>IBAN:</strong> {selectedVendor?.account?.iban}
                </p>
                <p>
                  <strong>Bank Name:</strong> {selectedVendor?.account?.bankName}
                </p>
                <p>
                  <strong>Currency:</strong> {selectedVendor?.account?.currency}
                </p>
                <p>
                  <strong>Address:</strong> {selectedVendor?.account?.address}
                </p>
                <p>
                  <strong>Bank Address:</strong>{" "}
                  {selectedVendor?.account?.bankaddress}
                </p>
                <p>
                  <strong>Country:</strong> {selectedVendor?.account?.country}
                </p>
              </div>
            </div>
          )}
        </div></div></>
  );
};

export default VendorsPayout;
